import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";
import { useLang } from "../i18n";
import { api } from "../api";
import PropertyCard from "../components/PropertyCard";

const HERO_BG = "https://images.pexels.com/photos/14395955/pexels-photo-14395955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2000";
const SIGNATURE_VIDEO = `${process.env.REACT_APP_BACKEND_URL}/api/static/signature.mp4?v=3`;
const LOUNGE_AUDIO = `${process.env.REACT_APP_BACKEND_URL}/api/static/lounge.mp3`;
const VIDEO_POSTER = "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1600&q=80";
const PORTRAIT = "https://customer-assets.emergentagent.com/job_exclusive-pm/artifacts/er3ywcms_20251230_104308000_iOS.jpg";
const AGENCY_LOGO = "https://customer-assets.emergentagent.com/job_exclusive-pm/artifacts/1l6tu2qx_20251205_225200000_iOS.jpg";

export default function Home() {
  const { t } = useLang();
  const [featured, setFeatured] = useState([]);
  const [sold, setSold] = useState([]);
  const audioRef = React.useRef(null);
  const [audioOn, setAudioOn] = React.useState(false);

  const toggleAudio = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (audioOn) {
      a.pause();
      setAudioOn(false);
    } else {
      try {
        a.muted = false;
        a.volume = 0.85;
        a.currentTime = a.currentTime || 0;
        await a.play();
        setAudioOn(true);
      } catch (err) {
        console.warn("Audio play blocked:", err);
        setAudioOn(false);
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [f, s] = await Promise.all([
          api.get("/properties", { params: { featured: true, limit: 6 } }),
          api.get("/properties", { params: { status_filter: "sold", limit: 9 } }),
        ]);
        setFeatured(f.data);
        setSold(s.data);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden grain pt-28 md:pt-0">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/65 to-black" />
        </div>
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center py-16">
          <div className="lg:col-span-8 text-left">
            <div className="eyebrow reveal mb-6">{t.hero.eyebrow}</div>
            <div className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#D4AF37] leading-[1] tracking-tight mb-3 reveal reveal-delay-1">
              Roland Azar
            </div>
            <a
              href="tel:5145725507"
              className="inline-block font-sans text-3xl md:text-4xl lg:text-5xl text-[#D4AF37] hover:text-[#E5C665] transition-colors tracking-wider mb-6 reveal reveal-delay-1 font-light"
              data-testid="hero-phone"
            >
              514.572.5507
            </a>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] text-[#F5F5F0] leading-[0.95] tracking-tight reveal reveal-delay-2">
              {t.hero.title}
            </h1>
            <span className="block gold-line my-10 reveal reveal-delay-2"></span>
            <p className="text-base md:text-lg text-white/70 max-w-xl leading-relaxed reveal reveal-delay-3">
              {t.hero.subtitle}
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 reveal reveal-delay-3">
              <Link to="/contact" className="btn-gold" data-testid="hero-cta-evaluation">{t.cta.private_evaluation}</Link>
              <Link to="/buy" className="btn-outline" data-testid="hero-cta-listings">{t.cta.view_listings}</Link>
            </div>
          </div>

          <div className="lg:col-span-4 reveal reveal-delay-3" data-testid="hero-portrait">
            <div className="relative mx-auto max-w-[280px] sm:max-w-[320px] lg:max-w-[360px]">
              {/* Gold frame accent */}
              <div className="absolute -top-4 -left-4 w-20 h-20 border-t border-l border-[#D4AF37]/70 z-10 pointer-events-none" />
              <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b border-r border-[#D4AF37]/70 z-10 pointer-events-none" />
              <div className="relative zoom-wrap aspect-[1/1] bg-[#141414] overflow-hidden">
                <img
                  src={PORTRAIT}
                  alt="Roland Azar"
                  className="zoom-img w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <div className="font-serif text-2xl md:text-3xl text-[#F5F5F0]">Roland Azar</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 text-[0.65rem] tracking-[0.3em] uppercase reveal reveal-delay-4 hidden md:block">
          Grand Montréal
        </div>
      </section>

      {/* APPROACH */}
      <section className="py-28 md:py-40 bg-[#0F0F0F]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow mb-4">{t.approach.eyebrow}</div>
            <span className="gold-line"></span>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#F5F5F0] leading-tight tracking-tight mb-8">
              {t.approach.title}
            </h2>
            <p className="text-white/60 text-lg leading-relaxed max-w-xl">{t.approach.body}</p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-28 md:py-36 bg-[#0B0B0B] border-t border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-20">
            <div className="eyebrow mb-4">{t.pillars.eyebrow}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5F5F0] leading-tight tracking-tight">
              {t.pillars.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {t.pillars.items.map((p, i) => (
              <div key={i} className="group" data-testid={`pillar-${i}`}>
                <div className="font-serif text-[#D4AF37] text-3xl mb-6">{p.n}</div>
                <span className="gold-line mb-8"></span>
                <h3 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mb-4">{p.t}</h3>
                <p className="text-white/60 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO */}
      <section className="py-28 md:py-36 bg-[#0F0F0F]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="eyebrow mb-4">{t.video.eyebrow}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5F5F0] tracking-tight">
              {t.video.title_prefix}{" "}
              <span className="font-signature text-[#D4AF37] text-6xl md:text-8xl ml-2 align-middle">{t.video.title_signature}</span>
            </h2>
          </div>
          <div className="relative aspect-video bg-black overflow-hidden" data-testid="video-player">
            <video
              src={SIGNATURE_VIDEO}
              poster={VIDEO_POSTER}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className="w-full h-full object-cover"
            />
            <audio ref={audioRef} src={LOUNGE_AUDIO} loop preload="auto" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
            <button
              onClick={toggleAudio}
              data-testid="audio-toggle"
              aria-label={audioOn ? "Mute lounge music" : "Play lounge music"}
              className={`absolute bottom-5 right-5 md:bottom-7 md:right-7 flex items-center gap-3 px-5 py-3 border transition-all duration-300 z-10 ${
                audioOn
                  ? "border-[#D4AF37] bg-[#D4AF37] text-black"
                  : "border-[#D4AF37]/70 bg-black/60 backdrop-blur-md text-[#D4AF37] hover:bg-[#D4AF37]/90 hover:text-black"
              }`}
            >
              {audioOn ? (
                <SpeakerHigh size={18} weight="bold" />
              ) : (
                <SpeakerSlash size={18} weight="bold" />
              )}
              <span className="text-[0.65rem] uppercase tracking-[0.2em] font-medium hidden sm:inline">
                {audioOn ? "Pause" : "Musique"}
              </span>
              {audioOn && (
                <span className="flex items-end gap-0.5 ml-1">
                  <span className="w-0.5 bg-black animate-[pulse_0.8s_ease-in-out_infinite] h-2"></span>
                  <span className="w-0.5 bg-black animate-[pulse_0.6s_ease-in-out_infinite] h-3"></span>
                  <span className="w-0.5 bg-black animate-[pulse_0.9s_ease-in-out_infinite] h-2.5"></span>
                </span>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* CAREER — 17 YEARS */}
      <section className="py-28 md:py-36 bg-[#0B0B0B] border-t border-white/5" data-testid="career-section">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="eyebrow mb-5">{t.career.eyebrow}</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#F5F5F0] tracking-tight leading-[1.05]">
              {t.career.title}
            </h2>
            <span className="block gold-line mx-auto mt-10"></span>
          </div>

          <div className="space-y-10 text-white/70 leading-[1.9] text-lg">
            <p>{t.career.p1}</p>
            <blockquote className="relative py-8 px-6 md:px-12 border-l-2 border-[#D4AF37] bg-[#141414]/60">
              <p className="font-serif italic text-2xl md:text-3xl text-[#F5F5F0] leading-snug">
                {t.career.pullquote}
              </p>
            </blockquote>
            <p>{t.career.p2}</p>
            <p className="font-serif text-xl md:text-2xl text-[#F5F5F0] italic leading-relaxed pt-4 border-t border-white/5">
              {t.career.p3}
            </p>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="py-28 md:py-36 bg-[#0B0B0B] border-t border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
              <div>
                <div className="eyebrow mb-4">{t.featured.eyebrow}</div>
                <h2 className="font-serif text-4xl md:text-5xl text-[#F5F5F0] tracking-tight">{t.featured.title}</h2>
              </div>
              <Link to="/buy" className="btn-outline" data-testid="home-view-all">{t.cta.view_listings}</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {featured.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* SOLD */}
      {sold.length > 0 && (
        <section className="py-28 md:py-36 bg-[#0F0F0F] border-t border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="max-w-2xl mb-16">
              <div className="eyebrow mb-4">{t.sold.eyebrow}</div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#F5F5F0] tracking-tight">{t.sold.title}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {sold.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      <section className="py-28 md:py-36 bg-[#0B0B0B] border-t border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="text-center mb-20">
            <div className="eyebrow mb-4">{t.testimonials.eyebrow}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5F5F0] tracking-tight">{t.testimonials.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {t.testimonials.items.map((tst, i) => (
              <figure key={i} className="text-center px-4" data-testid={`testimonial-${i}`}>
                <div className="text-[#D4AF37] font-serif text-5xl leading-none mb-4">“</div>
                <blockquote className="font-serif italic text-xl md:text-2xl text-[#F5F5F0] leading-relaxed mb-6">
                  {tst.q}
                </blockquote>
                <figcaption className="text-[0.7rem] uppercase tracking-[0.25em] text-white/50">{tst.a}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-32 md:py-44 overflow-hidden grain">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/9965979/pexels-photo-9965979.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1600" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/75" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <h2 className="font-serif text-4xl md:text-6xl text-[#F5F5F0] leading-tight tracking-tight mb-6">
            {t.finalCta.title}
          </h2>
          <p className="text-white/60 mb-10">{t.finalCta.sub}</p>
          <Link to="/contact" className="btn-gold" data-testid="home-final-cta">{t.finalCta.btn}</Link>
        </div>
      </section>
    </div>
  );
}
